export type EpsonDrawerConfig = {
  printerAddress: string;
  deviceId: string;
  drawerPin: 2 | 5;
};

export type EpsonDrawerPulseResult =
  | { outcome: "OPENED" }
  | { outcome: "FAILED"; errorCode: string; errorMessage: string };

const openedStoragePrefix = "cosmoEthernetCashDrawerOpened:";

export function buildEpsonDrawerEndpoint(
  config: Pick<EpsonDrawerConfig, "printerAddress" | "deviceId">,
  timeoutMs = 10_000,
): string {
  return `https://${config.printerAddress}/cgi-bin/epos/service.cgi?devid=${encodeURIComponent(config.deviceId)}&timeout=${timeoutMs}`;
}

export function buildEpsonDrawerXml(drawerPin: 2 | 5): string {
  const drawer = drawerPin === 2 ? "drawer_1" : "drawer_2";
  const request = `<epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print"><pulse drawer="${drawer}" time="pulse_100" /></epos-print>`;
  return `<?xml version="1.0" encoding="utf-8"?><s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/"><s:Body>${request}</s:Body></s:Envelope>`;
}

function printerResponseCode(xml: string): string | null {
  return xml.match(/\bcode\s*=\s*["']([^"']*)["']/iu)?.[1] || null;
}

export async function pulseEpsonCashDrawer(
  config: EpsonDrawerConfig,
  openingId: string,
): Promise<EpsonDrawerPulseResult> {
  const storageKey = `${openedStoragePrefix}${openingId}`;
  if (window.sessionStorage.getItem(storageKey) === "1") return { outcome: "OPENED" };

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 12_000);
  try {
    const response = await fetch(buildEpsonDrawerEndpoint(config), {
      method: "POST",
      mode: "cors",
      credentials: "omit",
      cache: "no-store",
      headers: {
        "Content-Type": "text/xml; charset=utf-8",
        "If-Modified-Since": "Thu, 01 Jan 1970 00:00:00 GMT",
        SOAPAction: '""',
      },
      body: buildEpsonDrawerXml(config.drawerPin),
      signal: controller.signal,
    });
    if (!response.ok) {
      return {
        outcome: "FAILED",
        errorCode: "PRINTER_HTTP_ERROR",
        errorMessage: `La impresora respondió con estado ${response.status}.`,
      };
    }
    const xml = await response.text();
    if (!/<response\b[^>]*\bsuccess\s*=\s*["']true["']/iu.test(xml)) {
      const code = printerResponseCode(xml);
      return {
        outcome: "FAILED",
        errorCode: "PRINTER_REJECTED_PULSE",
        errorMessage: code ? `La impresora rechazó el pulso (${code}).` : "La impresora rechazó el pulso de apertura.",
      };
    }
    window.sessionStorage.setItem(storageKey, "1");
    return { outcome: "OPENED" };
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      return { outcome: "FAILED", errorCode: "PRINTER_TIMEOUT", errorMessage: "La impresora no respondió dentro del tiempo esperado." };
    }
    return {
      outcome: "FAILED",
      errorCode: "PRINTER_CONNECTION_FAILED",
      errorMessage: "No se pudo establecer la conexión segura con la impresora Ethernet.",
    };
  } finally {
    window.clearTimeout(timeout);
  }
}
